import React from "react";
import { useLang } from "@/context/LanguageContext";

/**
 * Fallback pill — a tiny "EN" marker rendered next to a translated string
 * when the current site language cannot be honoured for that specific key
 * (i.e. renderedLang(k) !== current lang). No new lookup hierarchy is
 * introduced; this component only READS renderedLang, which follows the
 * existing LanguageContext lookup order.
 */
export const FallbackPill = ({ textKey }) => {
  const ctx = useLang();
  if (!ctx || !textKey) return null;
  const shown = ctx.renderedLang(textKey);
  if (!shown || shown === ctx.lang) return null;
  return (
    <span
      className="fallback-pill"
      aria-label={`Shown in ${shown.toUpperCase()} — translation pending`}
      title="Translation pending — showing English"
      data-testid={`fallback-pill-${textKey}`}
    >
      {shown.toUpperCase()}
    </span>
  );
};

/**
 * <T textKey="..." /> — renders t(textKey) and appends a fallback pill when
 * the rendered language differs from the selected language. Use in place of
 * bare {t("...")} where a visible fallback marker is desired.
 */
export const T = ({ textKey, fallback = "" }) => {
  const ctx = useLang();
  if (!ctx || !textKey) return null;
  const text = ctx.t(textKey, fallback);
  return (
    <>
      {text}
      <FallbackPill textKey={textKey} />
    </>
  );
};

export default T;
